import { Link } from "react-router-dom";
import { ShieldCheck, Lock, FileText, Mail, ArrowLeft } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { SITE } from "@/lib/site";

export default function Privacy() {
  return (
    <div className="min-h-screen">
      <Navigation />

      <section className="relative pt-28 md:pt-32 pb-10 md:pb-12">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="container mx-auto relative z-10">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/5 text-primary text-xs font-medium mb-5">
              <Lock className="w-3.5 h-3.5" /> Last updated: {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}
            </div>
            <h1 data-testid="privacy-title" className="font-display text-4xl sm:text-5xl md:text-6xl leading-[0.98] tracking-tight">
              Privacy <em className="not-italic gradient-text font-light">policy.</em>
            </h1>
            <p className="mt-5 text-muted-foreground text-pretty">
              Your privacy is important to us. This policy explains what information {SITE.name} collects through this website, how we use it, and your rights.
            </p>
          </div>
        </div>
      </section>

      <main className="pb-12 md:pb-16">
        <div className="container mx-auto">
          <article className="max-w-3xl mx-auto rounded-3xl border border-border bg-card/40 p-6 md:p-10 prose-content">
            <Section title="1. Information we collect">
              <p>
                We may collect the following information when you contact us, schedule an appointment, or otherwise interact with this website:
              </p>
              <ul>
                <li><strong>Contact details</strong> you provide (name, email address, phone number).</li>
                <li><strong>Appointment details</strong> you submit through our contact form or our scheduling tool (Calendly).</li>
                <li><strong>Limited technical data</strong> automatically collected by your browser (IP address, browser type, pages visited).</li>
              </ul>
              <p className="text-sm text-muted-foreground">
                We do <strong>not</strong> collect protected health information (PHI) through this website. Sensitive medical information is only collected through our secure clinical systems after you become an established patient.
              </p>
            </Section>

            <Section title="2. How we use your information">
              <p>We use the information you provide to:</p>
              <ul>
                <li>Respond to your inquiries and schedule appointments.</li>
                <li>Send appointment confirmations, reminders, and follow-ups.</li>
                <li>Improve and maintain this website.</li>
                <li>Comply with legal and professional obligations.</li>
              </ul>
              <p>We will <strong>never</strong> sell, rent, or share your personal information with third parties for marketing purposes.</p>
            </Section>

            <Section title="3. Third-party services">
              <p>
                We use a small number of trusted third-party tools to operate this website. Each has its own privacy policy:
              </p>
              <ul>
                <li><strong>Calendly</strong> — appointment scheduling. When you book a time, your name, email, and any details you provide are processed by Calendly.</li>
                <li><strong>Email service providers</strong> — used to send appointment confirmations and replies.</li>
              </ul>
            </Section>

            <Section title="4. HIPAA & clinical care">
              <p>
                Communications submitted through this public website (including the contact form) are <strong>not encrypted end-to-end</strong> and should not be used to share protected health information (PHI), diagnoses, medication details, or other sensitive medical content.
              </p>
              <p>
                Once you become a patient, all clinical communication takes place through our HIPAA-compliant electronic health record and secure patient portal. Our handling of PHI within those systems is governed by HIPAA and our separate <strong>Notice of Privacy Practices</strong>, which we will provide at the time of your first visit.
              </p>
            </Section>

            <Section title="5. Cookies">
              <p>
                This website uses minimal cookies — only those required for basic functionality and embedded scheduling. We do not use third-party advertising or behavioural tracking cookies.
              </p>
            </Section>

            <Section title="6. Your rights">
              <p>
                You may at any time:
              </p>
              <ul>
                <li>Request a copy of the information we hold about you.</li>
                <li>Ask us to correct or delete your information.</li>
                <li>Withdraw consent to receive communications.</li>
              </ul>
              <p>To exercise any of these rights, email us at <a href={`mailto:${SITE.email}`}>{SITE.email}</a>.</p>
            </Section>

            <Section title="7. Children's privacy">
              <p>
                Although we provide care to children and adolescents, this website is not directed at children under 13. We do not knowingly collect personal information from children online; appointment requests for minors should always be submitted by a parent or legal guardian.
              </p>
            </Section>

            <Section title="8. Changes to this policy">
              <p>
                We may update this Privacy Policy from time to time. The "Last updated" date at the top reflects the most recent revision. Material changes will be highlighted on this page.
              </p>
            </Section>

            <Section title="9. Contact us">
              <p>
                If you have questions about this Privacy Policy or how your information is handled, please contact:
              </p>
              <ul>
                <li>{SITE.name}</li>
                <li>{SITE.address}</li>
                <li>Email: <a href={`mailto:${SITE.email}`}>{SITE.email}</a></li>
                <li>Phone: <a href={`tel:${SITE.phoneRaw}`}>{SITE.phone}</a></li>
              </ul>
            </Section>

            <div className="mt-10 pt-6 border-t border-border/70 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <Button asChild variant="outline" className="rounded-full border-border h-11 px-5">
                <Link to="/"><ArrowLeft className="w-4 h-4 mr-2" /> Back home</Link>
              </Button>
              <Button asChild className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-5">
                <a href={`mailto:${SITE.email}?subject=${encodeURIComponent("Privacy question")}`}>
                  <Mail className="w-4 h-4 mr-2" /> Contact us
                </a>
              </Button>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8 last:mb-0">
      <h2 className="font-display text-xl md:text-2xl tracking-tight mb-3 text-foreground">{title}</h2>
      <div className="space-y-3 text-foreground/85 leading-relaxed [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_a:hover]:text-primary/80 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_strong]:text-foreground">
        {children}
      </div>
    </section>
  );
}
